import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import SpatialLayout from "@/components/SpatialLayout";
import Layout from "@/components/Layout";
import PageLoader from "@/components/ui/PageLoader"; // NEW IMPORT
import PageTransition from "@/components/ui/PageTransition";
import useSmoothScroll from "@/hooks/useSmoothScroll";
import ScrollProgress from "@/components/ui/ScrollProgress";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/utils/ScrollToTop";
import AmbientPlayer from "@/components/ui/AmbientPlayer";
import PrivacyBanner from "@/components/ui/PrivacyBanner";
import NotFound from "@/pages/NotFound";
import "@/App.css";

const HomePage = lazy(() => import("@/pages/Home"));
const HospitalsPage = lazy(() => import("@/pages/Hospitals"));
const CorporatesPage = lazy(() => import("@/pages/Corporates"));
const SchoolsPage = lazy(() => import("@/pages/Schools"));
const IndividualsPage = lazy(() => import("@/pages/Individuals"));
const SciencePage = lazy(() => import("@/pages/Science"));
const BetaPage = lazy(() => import("@/pages/Beta"));
const PrivacyPage = lazy(() => import("@/pages/Privacy"));
const TermsPage = lazy(() => import("@/pages/Terms"));
const BrandKit = lazy(() => import("@/pages/BrandKit"));
const ContactPage = lazy(() => import("@/pages/Contact"));

function AnimatedRoutes() {
  const location = useLocation();
  useSmoothScroll();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/hospitals" element={<PageTransition><HospitalsPage /></PageTransition>} />
        <Route path="/corporates" element={<PageTransition><CorporatesPage /></PageTransition>} />
        <Route path="/schools" element={<PageTransition><SchoolsPage /></PageTransition>} />
        <Route path="/individuals" element={<PageTransition><IndividualsPage /></PageTransition>} />
        <Route path="/science" element={<PageTransition><SciencePage /></PageTransition>} />
        <Route path="/beta" element={<PageTransition><BetaPage /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
        <Route path="/brand" element={<PageTransition><BrandKit /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <ScrollProgress />
          
          {/* Hide Ambient Player on Mobile (md:block) */}
          <div className="hidden md:block">
             <AmbientPlayer />
          </div>
          
          <SpatialLayout>
            <Layout>
              {/* USE PAGE LOADER INSTEAD OF NULL */}
              <Suspense fallback={<PageLoader />}>
                <AnimatedRoutes />
              </Suspense>
            </Layout>
          </SpatialLayout>
          
          <PrivacyBanner />
          
          <Toaster 
            position="top-center" 
            theme="system" 
            className="font-sans"
            toastOptions={{
              style: {
                background: 'var(--glass-bg)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-main)',
                backdropFilter: 'blur(10px)',
              }
            }}
          />
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
